// Discount codes: one store for the codes at the signed-in location, shared by
// Marketing › Discounts, the till, and the Social, email and text pickers.
// The server is the truth once discount/* exists; until then (staging only)
// the device stand-in keeps them. Every change is optimistic and goes back on
// failure. Booking pages call checkCode / holdCode with the salon being
// booked, not the signed-in one.
import { useEffect, useState } from 'react';
import moment from 'moment';
import { DISCOUNTS_STAND_IN } from '../../config/featureFlags';
import { discountService } from '../../_services/discount.service';
import { getAccess, isSelfEmployedMe, can } from '../permissions/access';
import { standIn } from './data/standIn';
import {
  cleanCode, codeKey, looksLikeGiftCard, quote, reasonText, shareable, stateOf, withHistory,
} from './data/codes';

// ── Who's signed in ─────────────────────────────────────────────────────────

const ls = (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } };
const storedUser = () => {
  try { return JSON.parse(localStorage.getItem('user')) || {}; } catch (e) { return {}; }
};

export const me = () => {
  const u = storedUser();
  const hd = u.hairdresser || (u.userInfo && u.userInfo.hairdresser) || {};
  const a = getAccess();
  const selfEmployed = isSelfEmployedMe();
  return {
    salonId: ls('salonId'),
    userId: ls('userId'),
    // The signed-in name is on the user record (the stylist record has none)
    name: u.name || hd.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '',
    owner: !!a.owner,
    selfEmployed,
  };
};

// Making and changing codes: the owner, a self-employed stylist (their own),
// or a role with the server's `discount_codes` capability (not on the server
// yet, so only owners and stylists until it is).
export const canManageCodes = () => {
  const m = me();
  return m.owner || m.selfEmployed || can('discount_codes');
};

// Can this person change this code? Salon codes: the owner or a manager with
// the capability. A stylist's own: only them.
export const canEdit = (code, who = me()) => {
  if (!code) return false;
  if (code.ownerType === 'stylist') return String(code.ownerId) === String(who.userId);
  return !who.selfEmployed && (who.owner || can('discount_codes'));
};

// ── The store ───────────────────────────────────────────────────────────────

let state = { salonId: null, codes: [], source: null, loaded: false, error: '' };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(state); } catch (e) { /* ignore */ } });
const set = (patch) => { state = { ...state, ...patch }; emit(); };

export const getState = () => state;
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const fallsBack = (err) => DISCOUNTS_STAND_IN && err && err.notThere;

let inflight = null;
export const hydrate = async ({ force = false } = {}) => {
  const salonId = ls('salonId');
  if (!salonId) return state;
  if (!force && state.loaded && state.salonId === salonId) return state;
  if (inflight && inflight.salonId === salonId) return inflight.p;
  if (state.salonId !== salonId) state = { salonId, codes: [], source: null, loaded: false, error: '' };
  const p = (async () => {
    try {
      const res = await discountService.list(salonId);
      const codes = Array.isArray(res) ? res : (res && res.codes) || [];
      set({ salonId, codes, source: 'server', loaded: true, error: '' });
    } catch (e) {
      if (fallsBack(e)) {
        const codes = await standIn.list(salonId);
        set({ salonId, codes, source: 'standin', loaded: true, error: '' });
      } else {
        set({ salonId, loaded: true, error: (e && e.plain && e.message) || 'Codes couldn’t load just now.' });
      }
    } finally {
      inflight = null;
    }
    return state;
  })();
  inflight = { salonId, p };
  return p;
};

const upsert = (code) => {
  const codes = state.codes.some((c) => c.id === code.id) ? state.codes.map((c) => (c.id === code.id ? code : c)) : [code, ...state.codes];
  set({ codes });
};

// Save a new code or a change. `action` and `note` go in its history.
export const saveCode = async (draft, { action, note } = {}) => {
  const who = me();
  const now = new Date();
  const isNew = !draft.id;
  let code = {
    ...draft,
    code: cleanCode(draft.code),
    salonId: draft.salonId || who.salonId,
    // A self-employed stylist only ever makes their own codes
    ownerType: who.selfEmployed ? 'stylist' : (draft.ownerType || 'salon'),
    ownerId: who.selfEmployed ? who.userId : (draft.ownerId || who.salonId),
    ownerName: who.selfEmployed ? (draft.ownerName || who.name) : (draft.ownerName || ''),
    createdAt: draft.createdAt || now.toISOString(),
    createdBy: draft.createdBy || who.userId,
    updatedAt: now.toISOString(),
  };
  if (code.ownerType === 'stylist') {
    code.fundingMode = 'stylist';
    code.scope = { ...(code.scope || {}), stylistIds: [String(code.ownerId)] };
  }
  code = withHistory(code, { by: who.userId, byName: who.name, action: action || (isNew ? 'created' : 'edited'), note: note || '' }, now);
  const before = state.codes;
  if (!isNew) upsert(code);
  try {
    let saved;
    if (state.source === 'standin') saved = await standIn.save(code);
    else {
      try {
        saved = await discountService.save(code);
        saved = (saved && (saved.code && typeof saved.code === 'object' ? saved.code : saved)) || code;
      } catch (e) {
        if (!fallsBack(e)) throw e;
        saved = await standIn.save(code);
        set({ source: 'standin' });
      }
    }
    upsert({ ...code, ...saved });
    return { ...code, ...saved };
  } catch (e) {
    set({ codes: before });
    throw e;
  }
};

// Pause, turn back on, or end early. Ending keeps bookings already made.
export const setStatus = async (code, status, note = '') => {
  const action = status === 'paused' ? 'paused' : status === 'ended' ? 'ended' : 'resumed';
  const next = { ...code, status, ...(status === 'ended' ? { endedOn: moment().format('YYYY-MM-DD') } : {}) };
  if (state.source !== 'server') return saveCode(next, { action, note });
  const who = me();
  const stamped = withHistory(next, { by: who.userId, byName: who.name, action, note });
  const before = state.codes;
  upsert(stamped);
  try {
    await discountService.status(code.id, status, note);
    return stamped;
  } catch (e) {
    if (fallsBack(e)) { set({ source: 'standin' }); return saveCode(next, { action, note }); }
    set({ codes: before });
    throw e;
  }
};

// ── Reading ─────────────────────────────────────────────────────────────────

export const useDiscountCodes = () => {
  const [s, setS] = useState(state);
  useEffect(() => {
    const off = subscribe(setS);
    hydrate().then(() => setS(getState()));
    return off;
  }, []);
  return { ...s, reload: () => hydrate({ force: true }) };
};

// The codes this person can put in a post, email or text: live or about to
// start, and their own (a stylist shares theirs, the salon shares its own).
export const pickableCodes = (codes = state.codes, who = me(), now = new Date()) => (codes || []).filter((c) => shareable(c, now) && (
  who.selfEmployed ? c.ownerType === 'stylist' && String(c.ownerId) === String(who.userId) : c.ownerType !== 'stylist'
));

export const useLiveCodes = () => {
  const s = useDiscountCodes();
  return { codes: pickableCodes(s.codes), loaded: s.loaded, source: s.source };
};

export const findById = (id) => state.codes.find((c) => c.id === id) || null;

// The code's current state, for warnings on scheduled posts and emails
export const codeStatusFor = (id, now = new Date()) => {
  const c = findById(id);
  return c ? stateOf(c, now) : null;
};

// ── Stylists' choices on salon codes ────────────────────────────────────────
//   { [stylistId]: { salonPays, shared, at } }   no entry = not answered yet

export const loadChoices = async (salonId = ls('salonId')) => {
  // The server's answers (owner / team: everyone; a stylist: their own)
  try {
    const res = await discountService.choices();
    return (res && res.choices) || {};
  } catch (e) {
    if (!fallsBack(e)) return {};
  }
  if (DISCOUNTS_STAND_IN) {
    const list = await standIn.choices(salonId);
    return Object.fromEntries(list.map((c) => [String(c.stylistId), { salonPays: !!c.salonPays, shared: !!c.shared, at: c.at }]));
  }
  return {};
};

// The stylist's own answer: 'salon' (only when the salon pays), 'shared'
// (sharing too), 'no'
export const saveMyChoice = async (answer) => {
  const who = me();
  const value = { salonPays: answer === 'salon' || answer === 'shared', shared: answer === 'shared' };
  try {
    await discountService.choice({ salonId: who.salonId, ...value });
    return value;
  } catch (e) {
    if (!fallsBack(e)) throw e;
  }
  if (DISCOUNTS_STAND_IN) await standIn.setChoice(who.salonId, who.userId, value);
  return value;
};

export const answerOf = (choice) => (!choice ? null : choice.shared ? 'shared' : choice.salonPays ? 'salon' : 'no');

// consentOf for quote(): self-employed stylists are skipped until they answer
export const consentFrom = (choices, selfEmployedIds) => {
  const se = new Set((selfEmployedIds || []).map(String));
  return (stylistId) => {
    const id = String(stylistId || '');
    if (!se.has(id)) return { selfEmployed: false };
    const c = (choices || {})[id];
    return { selfEmployed: true, salonPays: !!(c && c.salonPays), shared: !!(c && c.shared) };
  };
};

// ── Using a code (booking pages and the till) ───────────────────────────────

// Check a typed code against a basket at a location.
//   { salonId, typed, lines, date, channel, client: { key, isNew }, selfEmployedIds }
// -> quote() plus { code } (the public parts) and { source }
export const checkCode = async ({ salonId, typed, lines, date, channel, client = {}, selfEmployedIds = [], now = new Date() }) => {
  if (looksLikeGiftCard(typed)) return { ok: false, reason: 'gift_card', message: reasonText('gift_card'), amount: 0, lines: [] };
  if (cleanCode(typed).length < 3) return { ok: false, reason: 'not_found', message: reasonText('not_found'), amount: 0, lines: [] };
  try {
    const res = await discountService.validate({ salonId, code: cleanCode(typed), lines, date, channel, client: { key: client.key || null } });
    // The words are the app's (REASONS); the server sends the reason. (Its
    // response wrapper fills an empty `message` with "Api Result".)
    return { ...res, message: res && res.ok ? '' : reasonText(res && res.reason, res && res.code), source: 'server' };
  } catch (e) {
    if (!fallsBack(e)) {
      return { ok: false, reason: 'error', message: 'Codes can’t be checked just now. Try again in a moment.', amount: 0, lines: [] };
    }
  }
  const code = await standIn.findByCode(salonId, typed);
  if (!code) return { ok: false, reason: 'not_found', message: reasonText('not_found'), amount: 0, lines: [], source: 'standin' };
  const used = await standIn.usedBy(salonId, code.code, client.key);
  const choices = await loadChoices(salonId);
  const q = quote(code, {
    now, date, channel, lines,
    client: { isNew: client.isNew === undefined ? null : client.isNew, used },
    consentOf: consentFrom(choices, selfEmployedIds),
  });
  return { ...q, code: publicPart(code), source: 'standin' };
};

// What a code is, for the banner on the booking page (before there's a
// basket): found or not, the public part, and its state today.
export const lookupCode = async ({ salonId, typed, now = new Date() }) => {
  if (looksLikeGiftCard(typed)) return { found: false, reason: 'gift_card' };
  try {
    const res = await discountService.validate({ salonId, code: cleanCode(typed), lines: [], lookup: true });
    return { found: !!(res && res.code), code: (res && res.code) || null, state: (res && res.state) || null, source: 'server' };
  } catch (e) {
    if (!fallsBack(e)) return { found: false, reason: 'error' };
  }
  const code = await standIn.findByCode(salonId, typed);
  if (!code) return { found: false, reason: 'not_found', source: 'standin' };
  return { found: true, code: publicPart(code), state: stateOf(code, now), source: 'standin' };
};

// What a client may see of a code (never the stats, history or owner ids)
export const publicPart = (c) => (c ? {
  id: c.id, code: c.code, type: c.type, value: c.value, ownerType: c.ownerType, ownerName: c.ownerName,
  scope: c.scope, audience: c.audience, minSpend: c.minSpend, perClientCap: c.perClientCap, totalUsesCap: c.totalUsesCap,
  startsOn: c.startsOn, endsOn: c.endsOn, appointmentsBy: c.appointmentsBy, days: c.days, channels: c.channels, status: c.status,
  fundingMode: c.fundingMode,
} : null);

// Hold a use on a booking once it's made (the server does this itself from
// `promo` on the booking call; the stand-in needs telling).
export const holdCode = async ({ salonId, code, bookingId, clientKey, amount, total, isNew, channel }) => {
  if (!code || !bookingId) return;
  if (!DISCOUNTS_STAND_IN) return;
  const found = await standIn.findByCode(salonId, code.code || code);
  if (!found) return;
  await standIn.redeem({ codeId: found.id, code: found.code, salonId, bookingId, clientKey: clientKey || null, amount, total, isNew: !!isNew, channel });
  if (String(state.salonId) === String(salonId)) hydrate({ force: true });
};

export const releaseCode = async (bookingId) => {
  if (!bookingId) return;
  try { await discountService.release({ bookingId }); } catch (e) { /* the stand-in below */ }
  if (DISCOUNTS_STAND_IN) await standIn.release(bookingId);
};

// The code a booking was made with, if any (the stand-in remembers; the
// server will send it on the booking as `promo`)
export const codeOnBooking = async (booking) => {
  if (booking && booking.promo && booking.promo.code) return booking.promo;
  if (!DISCOUNTS_STAND_IN || !booking || !booking._id) return null;
  const use = await standIn.useFor(booking._id);
  return use ? { code: use.code, codeId: use.codeId, amount: use.amount } : null;
};

// A client's key for per-client limits: their phone in one form (07… and
// +447… are the same person), else their account.
export const clientKeyOf = ({ phone, userId } = {}) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length >= 10) {
    const uk = digits.startsWith('44') ? digits.slice(2) : digits.startsWith('0') ? digits.slice(1) : digits;
    return `tel:${uk}`;
  }
  return userId ? `user:${userId}` : null;
};

export const sameCode = (a, b) => codeKey(a) === codeKey(b);

// Tests only
export const _resetStore = () => { state = { salonId: null, codes: [], source: null, loaded: false, error: '' }; inflight = null; };
